import { G } from '@mobily/ts-belt';
import { useEffect } from 'react';
import { BooleanInput, required, SelectInput, TextInput } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { isBillingChannelWithPaper } from '@Common/validators/b2b_admin_correspondence';

import { billingChannelChoices } from '@sh4res/bo-common/choices/admins';
import { ShowSection, TypographyTranslated } from '@sh4res/bo-common/components';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { useCountryCodeChoicesByName } from '@sh4res/bo-common/utils/countryChoices';

type Kind = 'mailing' | 'billing';

const labelOf = (field: string) => `resources.bo_common.b2b.admins.fields.${field}`;

const isFilled = (value: unknown) => G.isNotNullable(value) && value !== '';

/**
 * "Addresses" step of the correspondent dialogs: optional mailing / billing address of the
 * correspondent, an empty address meaning that the company one is used.
 */
export const AdminCorrespondenceStep = ({
  testId,
  disabled = false,
}: {
  testId: string;
  disabled?: boolean;
}) => (
  <>
    <CorrespondenceSection kind="mailing" testId={testId} disabled={disabled} />
    <CorrespondenceSection kind="billing" testId={testId} disabled={disabled} />
  </>
);

const CorrespondenceSection = ({
  kind,
  testId,
  disabled,
}: {
  kind: Kind;
  testId: string;
  disabled: boolean;
}) => {
  const { setValue } = useFormContext();
  const countryChoices = useCountryCodeChoicesByName();
  const capitalizedKind = kind === 'mailing' ? 'Mailing' : 'Billing';
  // Static `data-testid` suffixes are enforced on the JSX attributes: the kind is resolved here
  const id = (suffix: string) => `${testId}_${suffix}`;

  const isCorrespondent = useWatch({ name: `is_${kind}_correspondent` });
  const [street, postalcode, city, country] = useWatch({
    name: [`${kind}_street`, `${kind}_postalcode`, `${kind}_city`, `${kind}_country`],
  });
  const isNpai = useWatch({ name: `is_npai_${kind}` });
  const npaiAt = useWatch({ name: `npai_${kind}_at` });
  const channel = useWatch({ name: 'billing_channel' });

  const filledCount = [street, postalcode, city, country].filter(isFilled).length;
  const isAddressComplete = filledCount === 4;
  const isPaper = kind === 'billing' && isBillingChannelWithPaper(channel);
  // All the address fields are needed as soon as one is filled, or when the invoices are sent by paper
  const isAddressRequired = Boolean(isCorrespondent) && (filledCount > 0 || isPaper);

  // Turning the correspondent off clears everything that depends on it
  useEffect(() => {
    if (isCorrespondent) {
      return;
    }
    const dependents: [name: string, current: unknown, empty: unknown][] = [
      [`${kind}_street`, street, null],
      [`${kind}_postalcode`, postalcode, null],
      [`${kind}_city`, city, null],
      [`${kind}_country`, country, null],
      [`is_npai_${kind}`, isNpai, false],
      [`npai_${kind}_at`, npaiAt, null],
      ...(kind === 'billing'
        ? [['billing_channel', channel, null] as [string, unknown, unknown]]
        : []),
    ];
    for (const [name, current, empty] of dependents) {
      if (isFilled(current) || current === true) {
        setValue(name, empty, { shouldValidate: true });
      }
    }
  }, [isCorrespondent, kind, street, postalcode, city, country, isNpai, npaiAt, channel, setValue]);

  // A PND needs a complete address to be about
  useEffect(() => {
    if (!isAddressComplete && isNpai) {
      setValue(`is_npai_${kind}`, false, { shouldValidate: true });
    }
  }, [isAddressComplete, isNpai, kind, setValue]);

  useEffect(() => {
    if (!isNpai && isFilled(npaiAt)) {
      setValue(`npai_${kind}_at`, null, { shouldValidate: true });
    }
  }, [isNpai, npaiAt, kind, setValue]);

  const addressProps = {
    defaultValue: null,
    validate: isAddressRequired ? [required()] : [],
    isRequired: isAddressRequired,
    ...inputDisabledProps(disabled),
  };

  return (
    <ShowSection
      leftAligned
      noCard
      label={`resources.bo_common.b2b.admins.correspondence.${kind}_title`}>
      <BooleanInput
        source={`is_${kind}_correspondent`}
        label={labelOf(`is_${kind}_correspondent`)}
        defaultValue={false}
        data-testid={id(`Is${capitalizedKind}CorrespondentInput`)}
        {...inputDisabledProps(disabled)}
      />
      {isCorrespondent && (
        <>
          {kind === 'billing' && (
            <SelectInput
              source="billing_channel"
              label={labelOf('billing_channel')}
              choices={billingChannelChoices}
              defaultValue={null}
              validate={[required()]}
              isRequired
              data-testid={`${testId}_BillingChannelInput`}
              {...inputDisabledProps(disabled)}
            />
          )}
          {!isAddressRequired && (
            <TypographyTranslated
              variant="body2"
              label="resources.bo_common.b2b.admins.correspondence.company_address_fallback"
            />
          )}
          <TextInput
            source={`${kind}_street`}
            label={labelOf(`${kind}_street`)}
            data-testid={id(`${capitalizedKind}StreetInput`)}
            {...addressProps}
          />
          <TextInput
            source={`${kind}_postalcode`}
            label={labelOf(`${kind}_postalcode`)}
            data-testid={id(`${capitalizedKind}PostalcodeInput`)}
            {...addressProps}
          />
          <TextInput
            source={`${kind}_city`}
            label={labelOf(`${kind}_city`)}
            data-testid={id(`${capitalizedKind}CityInput`)}
            {...addressProps}
          />
          <SelectInput
            source={`${kind}_country`}
            label={labelOf(`${kind}_country`)}
            choices={countryChoices}
            data-testid={id(`${capitalizedKind}CountryInput`)}
            {...addressProps}
          />
          {isAddressComplete && (
            <BooleanInput
              source={`is_npai_${kind}`}
              label={labelOf(`is_npai_${kind}`)}
              defaultValue={false}
              data-testid={id(`IsNpai${capitalizedKind}Input`)}
              {...inputDisabledProps(disabled)}
            />
          )}
          {isAddressComplete && isNpai && (
            <DateInput
              source={`npai_${kind}_at`}
              label={labelOf(`npai_${kind}_at`)}
              defaultValue={null}
              validate={[required()]}
              isRequired
              data-testid={id(`Npai${capitalizedKind}AtInput`)}
              {...inputDisabledProps(disabled)}
            />
          )}
        </>
      )}
    </ShowSection>
  );
};
